// src/components/HistoryModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, Table, Typography, Tabs, Spin, Button, message, 
  Tag, Select, Input, Popover, Tooltip, Radio, Dropdown,
  DatePicker, Space, Badge, Collapse, Row, Col
} from 'antd';
import { 
  EyeOutlined, EyeInvisibleOutlined, CopyOutlined, 
  PlusOutlined, TagsOutlined, CloseOutlined,
  DownloadOutlined, FilterOutlined, SearchOutlined,
  CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ApiOutlined, AppstoreOutlined, ClearOutlined
} from '@ant-design/icons';
import ReactAce from 'react-ace';
import axios from 'axios';
import { useTheme } from '../App';
import dayjs from 'dayjs';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-textmate';

const { Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Search } = Input;

function HistoryModal({ visible, onClose, memberName, API_BASE_URL, selectedEnv, onLoadRequestFromHistory, style }) {
  const [historyItems, setHistoryItems] = useState([]);
  const [allHistoryItems, setAllHistoryItems] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  
  // Use theme from context
  const { theme, currentTheme } = useTheme();
  
  // Existing state variables
  const [showApiKey, setShowApiKey] = useState(false);
  const [showBearer, setShowBearer] = useState(false);
  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [labelPopoverVisible, setLabelPopoverVisible] = useState({});
  const [labelFilterType, setLabelFilterType] = useState('AND');
  const [responseStats, setResponseStats] = useState({
    total: 0,
    successful: 0,
    failed: 0,
    avgResponseTime: 0
  });

  // New state variables for enhanced filtering
  const [apiNameSearch, setApiNameSearch] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'success', 'error'
  const [dateRange, setDateRange] = useState([null, null]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableApis, setAvailableApis] = useState([]);
  const [filterCollapsed, setFilterCollapsed] = useState(['filters']);

  // Enhanced filter function
  const applyFilters = useCallback(() => {
    let filtered = [...allHistoryItems];

    // API name search
    if (apiNameSearch.trim()) {
      filtered = filtered.filter(item => 
        item.selectedApi && item.selectedApi.toLowerCase().includes(apiNameSearch.toLowerCase())
      );
    }

    // Group filter
    if (selectedGroups.length > 0) {
      filtered = filtered.filter(item => 
        item.selectedGroup && selectedGroups.includes(item.selectedGroup)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => {
        const isError = item.responseData && item.responseData.error;
        return statusFilter === 'error' ? isError : !isError;
      });
    }

    // Date range filter - Fixed to use dayjs
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dayjs(dateRange[0]).startOf('day');
      const endDate = dayjs(dateRange[1]).endOf('day');
      filtered = filtered.filter(item => {
        const itemDate = dayjs(item.timestamp);
        return itemDate.isAfter(startDate) && itemDate.isBefore(endDate);
      });
    }

    // Label filter (existing logic)
    if (selectedLabels.length > 0) {
      filtered = filtered.filter(item => {
        const itemLabels = Array.isArray(item.labels) 
          ? item.labels.filter(label => label && typeof label === 'string' && label.trim() !== '')
          : [];
        
        if (labelFilterType === 'AND') {
          return selectedLabels.every(selectedLabel => 
            itemLabels.includes(selectedLabel)
          );
        } else {
          return selectedLabels.some(selectedLabel => 
            itemLabels.includes(selectedLabel)
          );
        }
      });
    }

    setHistoryItems(filtered);
    calculateStats(filtered);
  }, [allHistoryItems, apiNameSearch, selectedGroups, statusFilter, dateRange, selectedLabels, labelFilterType]);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        memberName,
        selectedEnv,
      };
      
      const response = await axios.get(`${API_BASE_URL}/history`, { params });
      setAllHistoryItems(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      message.error('Failed to fetch history');
    }
    setLoading(false);
  }, [API_BASE_URL, memberName, selectedEnv]);

  const fetchAllLabels = useCallback(async () => {
    try {
      const params = {
        memberName,
        selectedEnv,
      };
      
      const response = await axios.get(`${API_BASE_URL}/history/labels`, { params });
      const validLabels = response.data.filter(label => 
        label && 
        typeof label === 'string' && 
        label.trim() !== ''
      );
      setAllLabels(validLabels);
    } catch (error) {
      console.error('Failed to fetch labels:', error);
      message.error('Failed to fetch labels');
    }
  }, [API_BASE_URL, memberName, selectedEnv]);

  const fetchAvailableGroups = useCallback(async () => {
    try {
      const params = {
        memberName,
        selectedEnv,
      };
      
      const response = await axios.get(`${API_BASE_URL}/history/groups`, { params });
      setAvailableGroups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setAvailableGroups([]);
    }
  }, [API_BASE_URL, memberName, selectedEnv]);

  const fetchAvailableApis = useCallback(async () => {
    try {
      const params = {
        memberName,
        selectedEnv,
      };
      
      const response = await axios.get(`${API_BASE_URL}/history/apis`, { params });
      setAvailableApis(response.data || []);
    } catch (error) {
      console.error('Failed to fetch APIs:', error);
      setAvailableApis([]);
    }
  }, [API_BASE_URL, memberName, selectedEnv]);

  useEffect(() => {
    if (visible && memberName && selectedEnv) {
      fetchHistory();
      fetchAllLabels();
      fetchAvailableGroups();
      fetchAvailableApis();
    } else {
      // Reset all states when modal is closed
      setHistoryItems([]);
      setAllHistoryItems([]);
      setSelectedHistoryItem(null);
      setSelectedRowKey(null);
      setShowApiKey(false);
      setShowBearer(false);
      setSelectedLabels([]);
      setApiNameSearch('');
      setSelectedGroups([]);
      setStatusFilter('all');
      setDateRange([null, null]);
    }
  }, [visible, memberName, selectedEnv, fetchHistory, fetchAllLabels, fetchAvailableGroups, fetchAvailableApis]);

  const calculateStats = useCallback((histories) => {
    const stats = {
      total: histories.length,
      successful: 0,
      failed: 0,
      avgResponseTime: 0
    };
    
    let totalResponseTime = 0;
    histories.forEach(history => {
      if (history.responseData && !history.responseData.error) {
        stats.successful++;
      } else {
        stats.failed++;
      }
      
      if (history.responseTime) {
        totalResponseTime += history.responseTime;
      }
    });
    
    stats.avgResponseTime = histories.length > 0 
      ? Math.round(totalResponseTime / histories.length) 
      : 0;
    
    setResponseStats(stats);
  }, []);

  const clearAllFilters = useCallback(() => {
    setApiNameSearch('');
    setSelectedGroups([]);
    setStatusFilter('all');
    setDateRange([null, null]);
    setSelectedLabels([]);
  }, []);

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (apiNameSearch.trim()) count++;
    if (selectedGroups.length > 0) count++;
    if (statusFilter !== 'all') count++;
    if (dateRange && dateRange[0] && dateRange[1]) count++;
    if (selectedLabels.length > 0) count++;
    return count;
  }, [apiNameSearch, selectedGroups, statusFilter, dateRange, selectedLabels]);

  // Keep all existing functions (downloadHistoryAsCSV, downloadHistoryAsJSON, etc.)
  const downloadHistoryAsCSV = useCallback(() => {
    try {
      const csvHeaders = [
        'Timestamp',
        'API',
        'Group',
        'Method',
        'Route',
        'Environment',
        'Status',
        'Response Time (ms)',
        'Labels',
        'Request Data',
        'Response Data'
      ];

      const csvRows = historyItems.map(item => {
        const status = item.responseData && !item.responseData.error ? 'Success' : 'Error';
        const labels = Array.isArray(item.labels) ? item.labels.join('; ') : '';
        const requestData = JSON.stringify(item.requestData || {});
        const responseData = JSON.stringify(item.responseData || {});

        return [
          new Date(item.timestamp).toLocaleString(),
          item.selectedApi || '',
          item.selectedGroup || '',
          item.method || '',
          item.route || '',
          item.selectedEnv || '',
          status,
          item.responseTime || '',
          labels,
          `"${requestData.replace(/"/g, '""')}"`,
          `"${responseData.replace(/"/g, '""')}"`,
        ];
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `specter_history_${memberName}_${selectedEnv}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('History downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      message.error('Failed to download history');
    }
  }, [historyItems, memberName, selectedEnv]);

  const downloadHistoryAsJSON = useCallback(() => {
    try {
      const exportData = {
        exportInfo: {
          memberName,
          environment: selectedEnv,
          exportDate: new Date().toISOString(),
          totalItems: historyItems.length,
          filters: {
            apiNameSearch,
            selectedGroups,
            statusFilter,
            dateRange: dateRange && dateRange.map(d => d ? d.toISOString() : null),
            labels: selectedLabels,
            labelFilterType
          }
        },
        history: historyItems.map(item => ({
          ...item,
          apiKey: undefined,
          bearerToken: undefined,
          authToken: undefined
        }))
      };

      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `specter_history_${memberName}_${selectedEnv}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('History downloaded successfully');
    } catch (error) {
      console.error('Error downloading JSON:', error);
      message.error('Failed to download history');
    }
  }, [historyItems, memberName, selectedEnv, apiNameSearch, selectedGroups, statusFilter, dateRange, selectedLabels, labelFilterType]);

  const updateLabels = async (itemId, labels) => {
    try {
      const cleanLabels = labels
        .filter(label => label && typeof label === 'string' && label.trim() !== '')
        .map(label => label.trim());
      
      await axios.patch(`${API_BASE_URL}/history/${itemId}/labels`, { labels: cleanLabels });
      
      setAllHistoryItems(prev => prev.map(item => 
        item._id === itemId ? { ...item, labels: cleanLabels } : item
      ));
      
      if (selectedHistoryItem?._id === itemId) {
        setSelectedHistoryItem({ ...selectedHistoryItem, labels: cleanLabels });
      }
      
      message.success('Labels updated successfully');
      fetchAllLabels();
    } catch (error) {
      console.error('Error updating labels:', error);
      message.error('Failed to update labels');
    }
  };

  const labelPopoverContent = (item) => (
    <div style={{ 
      width: 250, 
      backgroundColor: theme.surface, 
      padding: '8px',
      borderRadius: '4px',
    }}>
      <Select
        mode="tags"
        style={{ width: '100%', marginBottom: 8 }}
        placeholder="Add labels..."
        value={item.labels || []}
        onChange={(value) => updateLabels(item._id, value)}
        options={allLabels.map(label => ({ value: label, label }))}
        filterOption={(input, option) => 
          option.value && 
          option.value.toLowerCase().includes(input.toLowerCase())
        }
      />
      <Input
        placeholder="Create new label"
        value={newLabel}
        onChange={(e) => setNewLabel(e.target.value)}
        onPressEnter={() => addNewLabel(item._id)}
        style={{
          backgroundColor: theme.surfaceLight,
          borderColor: theme.border,
          color: theme.text,
        }}
        suffix={
          <Button 
            type="link" 
            size="small"
            onClick={() => addNewLabel(item._id)}
            style={{ color: theme.primary }}
          >
            Add
          </Button>
        }
      />
    </div>
  );

  const addNewLabel = async (itemId) => {
    if (newLabel.trim()) {
      const currentItem = allHistoryItems.find(item => item._id === itemId);
      const currentLabels = currentItem?.labels || [];
      const cleanLabel = newLabel.trim();
      
      if (!currentLabels.includes(cleanLabel)) {
        await updateLabels(itemId, [...currentLabels, cleanLabel]);
      }
      setNewLabel('');
    }
  };

  const onRowClick = (record) => {
    setSelectedHistoryItem(record);
    setSelectedRowKey(record._id); 
    setShowApiKey(false);
    setShowBearer(false);
  };

  const maskString = (str) => {
    if (!str) return '';
    return '*'.repeat(Math.max(str.length, 6));
  };

  const handleCopy = (textToCopy) => {
    navigator.clipboard.writeText(textToCopy).then(
      () => { message.success('Copied to clipboard'); },
      () => { message.error('Failed to copy'); }
    );
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 120,
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      defaultSortOrder: 'descend',
    },
    {
      title: 'API',
      dataIndex: 'selectedApi',
      key: 'selectedApi',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
      sorter: (a, b) => (a.selectedApi || '').localeCompare(b.selectedApi || ''),
    },
    {
      title: 'Group',
      dataIndex: 'selectedGroup',
      key: 'selectedGroup',
      width: 120,
      ellipsis: true,
      render: (text) => text || <Text type="secondary">N/A</Text>,
      sorter: (a, b) => (a.selectedGroup || '').localeCompare(b.selectedGroup || ''),
    },
    {
      title: 'Status',
      dataIndex: 'responseData',
      key: 'status',
      width: 100,
      render: (responseData) => {
        if (!responseData) return <Tag color="default">No Response</Tag>;
        const isError = responseData.error;
        return isError ? 
          <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag> : 
          <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>;
      },
    },
    {
      title: 'Time',
      dataIndex: 'responseTime',
      key: 'responseTime',
      width: 100,
      render: (time) => time ? `${time}ms` : <Text type="secondary">-</Text>,
      sorter: (a, b) => (a.responseTime || 0) - (b.responseTime || 0),
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      width: 180,
      render: (labels, record) => {
        const validLabels = Array.isArray(labels) 
          ? labels.filter(label => label && typeof label === 'string' && label.trim() !== '')
          : [];
          
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {validLabels.slice(0, 2).map(label => (
              <Tag key={label} style={{ margin: 0, fontSize: '11px' }}>{label}</Tag>
            ))}
            {validLabels.length > 2 && (
              <Tooltip 
                title={
                  <div style={{ padding: '4px 0' }}>
                    {validLabels.slice(2).map(label => (
                      <Tag key={label} style={{ margin: '2px', display: 'inline-block' }}>
                        {label}
                      </Tag>
                    ))}
                  </div>
                }
                placement="top"
              >
                <Tag style={{ margin: 0, cursor: 'help', fontSize: '11px' }}>+{validLabels.length - 2}</Tag>
              </Tooltip>
            )}
            <Popover
              content={labelPopoverContent(record)}
              title="Manage Labels"
              trigger="click"
              placement="left"
              visible={labelPopoverVisible[record._id]}
              onVisibleChange={(visible) => 
                setLabelPopoverVisible({...labelPopoverVisible, [record._id]: visible})
              }
            >
              <Button 
                type="link" 
                size="small" 
                icon={<TagsOutlined />}
                style={{ padding: '0 4px', minWidth: 'auto' }}
              />
            </Popover>
          </div>
        );
      },
    },
  ];

  const requestBodyString = selectedHistoryItem && selectedHistoryItem.requestData
    ? JSON.stringify(selectedHistoryItem.requestData, null, 2)
    : '{}';

  const hasApiKey = selectedHistoryItem?.apiKey && selectedHistoryItem.apiKey.trim() !== '';
  const hasBearer = selectedHistoryItem?.bearerToken && selectedHistoryItem.bearerToken.trim() !== '';

  const downloadMenuItems = [
    {
      key: 'csv',
      label: 'Download as CSV',
      icon: <DownloadOutlined />,
      onClick: downloadHistoryAsCSV,
    },
    {
      key: 'json',
      label: 'Download as JSON',
      icon: <DownloadOutlined />,
      onClick: downloadHistoryAsJSON,
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Request History</span>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, paddingRight: 32 }}>
            <Tag>Total: {responseStats.total}</Tag>
            <Tag color="green">Success: {responseStats.successful}</Tag>
            <Tag color="red">Failed: {responseStats.failed}</Tag>
            <Tag color="blue">Avg Time: {responseStats.avgResponseTime}ms</Tag>
          </div>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width="90%"
      style={style} 
      footer={null}
      bodyStyle={{ padding: '16px', backgroundColor: theme.surface }}
    >
      {/* Enhanced Filter Panel */}
      <Collapse 
        activeKey={filterCollapsed} 
        onChange={setFilterCollapsed}
        style={{ marginBottom: 16, backgroundColor: theme.surfaceLight }}
      >
        <Panel 
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <FilterOutlined />
                <span>Advanced Filters</span>
                {getActiveFilterCount() > 0 && (
                  <Badge count={getActiveFilterCount()} style={{ backgroundColor: theme.primary }} />
                )}
              </Space>
            </div>
          } 
          key="filters"
        >
          <div style={{ padding: '8px 0' }}>
            <Row gutter={[12, 12]}>
              {/* First Row - Main Filters */}
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text style={{ color: theme.text, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ApiOutlined /> Search API Name
                  </Text>
                </div>
                <Search
                  placeholder="Search by API name..."
                  value={apiNameSearch}
                  onChange={(e) => setApiNameSearch(e.target.value)}
                  allowClear
                  size="small"
                  style={{ width: '100%' }}
                />
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text style={{ color: theme.text, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <AppstoreOutlined /> Filter by Groups
                  </Text>
                </div>
                <Select
                  mode="multiple"
                  size="small"
                  style={{ width: '100%' }}
                  placeholder="Select groups..."
                  value={selectedGroups}
                  onChange={setSelectedGroups}
                  options={availableGroups.map(group => ({ value: group, label: group }))}
                  allowClear
                  maxTagCount="responsive"
                />
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text style={{ color: theme.text, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircleOutlined /> Status
                  </Text>
                </div>
                <Radio.Group 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  buttonStyle="solid"
                  size="small"
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Radio.Button value="all" style={{ flex: 1, textAlign: 'center' }}>All</Radio.Button>
                  <Radio.Button value="success" style={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutlined /> Success
                  </Radio.Button>
                  <Radio.Button value="error" style={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CloseCircleOutlined /> Error
                  </Radio.Button>
                </Radio.Group>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text style={{ color: theme.text, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CalendarOutlined /> Date Range
                  </Text>
                </div>
                <RangePicker
                  size="small"
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  allowClear
                  placeholder={['Start date', 'End date']}
                />
              </Col>

              {/* Second Row - Labels and Actions */}
              <Col xs={24} sm={16}>
                <div style={{ marginBottom: 4 }}>
                  <Text style={{ color: theme.text, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <TagsOutlined /> Filter by Labels
                  </Text>
                </div>
                <Space.Compact style={{ width: '100%' }}>
                  <Select
                    mode="multiple"
                    size="small"
                    style={{ width: selectedLabels.length > 1 ? 'calc(100% - 100px)' : '100%' }}
                    placeholder="Select labels to filter"
                    value={selectedLabels}
                    onChange={setSelectedLabels}
                    options={allLabels.map(label => ({ value: label, label }))}
                    allowClear
                    maxTagCount="responsive"
                  />
                  {selectedLabels.length > 1 && (
                    <Radio.Group
                      value={labelFilterType}
                      onChange={(e) => setLabelFilterType(e.target.value)}
                      buttonStyle="solid"
                      size="small"
                    >
                      <Radio.Button value="AND">AND</Radio.Button>
                      <Radio.Button value="OR">OR</Radio.Button>
                    </Radio.Group>
                  )}
                </Space.Compact>
              </Col>

              <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, justifyContent: 'flex-end' }}>
                <Button 
                  size="small"
                  onClick={clearAllFilters} 
                  icon={<ClearOutlined />}
                  disabled={getActiveFilterCount() === 0}
                >
                  Clear All
                </Button>
                <Dropdown
                  menu={{ items: downloadMenuItems }}
                  placement="bottomRight"
                  disabled={historyItems.length === 0}
                >
                  <Button size="small" type="primary" icon={<DownloadOutlined />}>
                    Export
                  </Button>
                </Dropdown>
              </Col>
            </Row>
          </div>
        </Panel>
      </Collapse>

      <div style={{ display: 'flex', gap: '16px', height: '60vh' }}>
        <div style={{ flex: 1.5, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              dataSource={historyItems}
              columns={columns}
              rowKey={(record) => record._id}
              size="small"
              onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
              })}
              rowClassName={(record) =>
                record._id === selectedRowKey ? 'ant-table-row-selected' : ''
              }
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                showQuickJumper: true,
              }}
              scroll={{ x: 800 }}
            />
          )}
        </div>

        {/* Details Panel */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: `1px solid ${theme.border}`,
            borderRadius: '4px',
            padding: '16px',
            background: theme.surfaceLight,
            color: theme.text,
          }}
        >
          {selectedHistoryItem ? (
            <>
              {/* Metadata Section */}
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  background: theme.surface,
                }}
              >
                <Text strong style={{ fontSize: '16px', display:'block', marginBottom:'12px', color: theme.text }}>
                  Request Metadata
                </Text>

                {hasApiKey && (
                  <div style={{ display: 'flex', alignItems: 'center', gap:'8px', marginBottom:'8px' }}>
                    <Text strong style={{flexShrink:0, color: theme.text}}>API Key:</Text>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0, flex:1 }}>
                      <div style={{ flex:'1 1 auto', overflowX:'auto', whiteSpace:'nowrap' }}>
                        <Text code style={{
                          display:'inline-block', 
                          backgroundColor: theme.surface,
                          color: theme.accent,
                          border: `1px solid ${theme.border}`
                        }}>
                          {showApiKey ? selectedHistoryItem.apiKey : maskString(selectedHistoryItem.apiKey)}
                        </Text>
                      </div>
                      <Button
                        type="text"
                        icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowApiKey(!showApiKey)}
                        style={{ color: theme.textSecondary }}
                      />
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(selectedHistoryItem.apiKey)}
                        style={{ color: theme.primary }}
                      />
                    </div>
                  </div>
                )}

                {hasBearer && (
                  <div style={{ display: 'flex', alignItems: 'center', gap:'8px', marginBottom:'8px' }}>
                    <Text strong style={{flexShrink:0, color: theme.text}}>Bearer:</Text>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0, flex:1 }}>
                      <div style={{ flex:'1 1 auto', overflowX:'auto', whiteSpace:'nowrap' }}>
                        <Text code style={{
                          display:'inline-block',
                          backgroundColor: theme.surface,
                          color: theme.accent,
                          border: `1px solid ${theme.border}`
                        }}>
                          {showBearer ? selectedHistoryItem.bearerToken : maskString(selectedHistoryItem.bearerToken)}
                        </Text>
                      </div>
                      <Button
                        type="text"
                        icon={showBearer ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowBearer(!showBearer)}
                        style={{ color: theme.textSecondary }}
                      />
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(selectedHistoryItem.bearerToken)}
                        style={{ color: theme.primary }}
                      />
                    </div>
                  </div>
                )}

                {selectedHistoryItem.selectedGroup && (
                  <div style={{ display:'flex', alignItems:'center', marginBottom:'8px' }}>
                    <Text strong style={{ width:'90px', flexShrink:0, color: theme.text }}>Group:</Text>
                    <div style={{ flex:'1', overflowX:'auto', whiteSpace:'nowrap' }}>
                      <Text code style={{
                        display:'inline-block',
                        backgroundColor: theme.surface,
                        color: theme.accent,
                        border: `1px solid ${theme.border}`
                      }}>
                        {selectedHistoryItem.selectedGroup}
                      </Text>
                    </div>
                  </div>
                )}

                {selectedHistoryItem.selectedApi && (
                  <div style={{ display:'flex', alignItems:'center', marginBottom:'8px' }}>
                    <Text strong style={{ width:'90px', flexShrink:0, color: theme.text }}>API:</Text>
                    <div style={{ flex:'1', overflowX:'auto', whiteSpace:'nowrap' }}>
                      <Text code style={{
                        display:'inline-block',
                        backgroundColor: theme.surface,
                        color: theme.accent,
                        border: `1px solid ${theme.border}`
                      }}>
                        {selectedHistoryItem.selectedApi}
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              <Tabs 
                defaultActiveKey="requestData" 
                style={{ height: '100%' }}
                tabBarStyle={{ color: theme.text }}
              >
                <TabPane tab="Request Data" key="requestData">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text type="secondary" style={{ color: theme.textSecondary }}>
                      Here is the JSON data that was sent during this request.
                    </Text>
                  
                    <Button
                      type="link"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopy(requestBodyString)}
                      style={{ color: theme.primary }}
                    >
                      Copy
                    </Button>
                  </div>
                  
                  <ReactAce
                    mode="json"
                    theme={currentTheme === 'dark' ? 'monokai' : 'textmate'}
                    value={requestBodyString}
                    readOnly
                    fontSize={14}
                    width="100%"
                    height="300px"
                    setOptions={{ 
                      useWorker: false,
                      showGutter: true,
                      highlightActiveLine: false,
                      showPrintMargin: false,
                    }}
                    style={{
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                    }}
                    key={selectedRowKey}
                  />

                  <div style={{ textAlign: 'right', padding: '16px' }}>
                    <Button
                      type="primary"
                      onClick={() => onLoadRequestFromHistory(selectedHistoryItem)}
                      style={{
                        backgroundColor: theme.primary,
                        borderColor: theme.primary,
                      }}
                    >
                      Load & Edit
                    </Button>
                  </div>
                </TabPane>

                <TabPane tab="Response Data" key="responseData">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text type="secondary" style={{ color: theme.textSecondary }}>
                      Here is the response data that was received from this request.
                    </Text>
                    <div>
                      {selectedHistoryItem.responseTime && (
                        <Tag color="blue" style={{ marginRight: 8 }}>
                          {selectedHistoryItem.responseTime}ms
                        </Tag>
                      )}
                      <Button
                        type="link"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(JSON.stringify(selectedHistoryItem.responseData, null, 2))}
                        style={{ color: theme.primary }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  
                  <ReactAce
                    mode="json"
                    theme={currentTheme === 'dark' ? 'monokai' : 'textmate'}
                    value={JSON.stringify(selectedHistoryItem.responseData || {}, null, 2)}
                    readOnly
                    fontSize={14}
                    width="100%"
                    height="300px"
                    setOptions={{ 
                      useWorker: false,
                      showGutter: true,
                      highlightActiveLine: false,
                      showPrintMargin: false,
                    }}
                    style={{
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                    }}
                    key={selectedRowKey + '_response'}
                  />
                </TabPane>
              </Tabs>
            </>
          ) : (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
              <Text type="secondary" style={{ color: theme.textSecondary }}>
                Select a record from the table to view details
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default HistoryModal;